import React, { useEffect } from "react";
import { Provider } from "react-redux";
import "./productCarousel.scss";
import { cls } from "cls";
import Store from "~/common/Store";
import CarouselMember from "./Carousel/CarouselMember";
import Stars from "~/common/Stars";
import { OtherProductsCarouselPromotion } from "./ProductDetailPageResult";
import OtherProductsCarouselPromotionMember from "./OtherProductsCarouselPromotionMember";
import { CarouselItem } from "./Carousel/CarouselItem";
import NoScriptLazyImage from "~/common/NoScriptLazyImage";

async function importCarouselFunctionality() {
  if (document.querySelector("[data-component=carousel]")) {
    const { default: initCategoryCarousels } = await import(
      "~/common/initCategoryCarousels"
    );
    initCategoryCarousels();
  }
}

interface Props {
  id: string;
  carouselItems: readonly CarouselItem[];
  heading: string;
  carouselPromotionMember?: OtherProductsCarouselPromotion;
  dataComponentName?: string;
  supportsRequestQuoteProgram?: boolean;
  gtmCarouselName?: string;
  gtmCarouselModel?: string;
}

const TallCarouselNoCartButton = ({
  id,
  carouselItems,
  heading,
  carouselPromotionMember,
  dataComponentName,
  supportsRequestQuoteProgram = false,
  gtmCarouselName,
  gtmCarouselModel,
}: Props) => {
  const hasActiveSale = carouselItems.some((item) => item.displaySalePrice);

  useEffect(() => {
    importCarouselFunctionality();
  }, [carouselItems]);

  return (
    <Provider store={Store}>
      <div className="carousel-wrap" data-component={dataComponentName} id={id}>
        <h2
          className={cls(
            "clears",
            "text-lg",
            "leading-5",
            "my-4",
            "ml-4",
            "xs:ml-0",
          )}
        >
          {heading}
        </h2>
        <div
          id="other-products"
          className="basic-carousel other-products other-products--vertical carousel-box init"
          data-component="carousel"
          gtm-carousel-name={gtmCarouselName}
          gtm-carousel-model={gtmCarouselModel}
        >
          <div className="ag-carousel-nav">
            <button type="button" className="ag-car-prev" data-control="prev">
              <span className="sr-only">show previous carousel items</span>
            </button>
          </div>
          <div id={id} className="ag-carousel-frame box" data-testid={id}>
            <ul
              className={cls(
                "ag-carousel",
                "ag-full",
                hasActiveSale && cls("min-h-[272px]", "max-h-[272px]"),
              )}
            >
              {carouselPromotionMember?.showShopAllTile && (
                <OtherProductsCarouselPromotionMember
                  image={carouselPromotionMember.image}
                  url={carouselPromotionMember.url}
                  text={carouselPromotionMember.text}
                  isTallCarouselMember
                />
              )}
              {carouselItems.map((carouselItem, index) =>
                supportsRequestQuoteProgram &&
                carouselItem.isRequestQuoteEnabled ? (
                  <li
                    className="ag-item gtm-item"
                    data-item-number={carouselItem.itemNumber}
                    gtm-tracking-number={carouselItem.itemNumber}
                  >
                    <div className="product-image">
                      <a
                        href={carouselItem.link}
                        className="image companionItemClicked"
                        title={carouselItem.description}
                        data-label={carouselItem.feedIdentifier}
                        data-action="click"
                      >
                        <NoScriptLazyImage
                          alt={carouselItem.description}
                          src={carouselItem.imagePath}
                        />
                      </a>
                    </div>
                    <div className="details">
                      <div className="ribbon-stack">&nbsp;</div>
                      <Stars rating={carouselItem.overallRating} />
                      <a
                        href={carouselItem.link}
                        className="description companionItemClicked"
                        title={carouselItem.description}
                        data-label={carouselItem.feedIdentifier}
                        data-action="click"
                      >
                        {carouselItem.description}
                      </a>
                      <div className="pricing" data-component="Pricing">
                        <p className="price text-gray-800 text-xs font-bold">
                          Contact for details
                        </p>
                      </div>
                    </div>
                  </li>
                ) : (
                  <CarouselMember
                    activeSalePricingClassNames={hasActiveSale ? "h-10" : ""}
                    item={carouselItem}
                    showAddToCart={false}
                    key={index}
                  />
                ),
              )}
            </ul>
          </div>
          <div className="ag-carousel-nav">
            <button type="button" className="ag-car-next" data-control="next">
              <span className="sr-only">show more carousel items</span>
            </button>
          </div>
        </div>
      </div>
    </Provider>
  );
};

export default TallCarouselNoCartButton;
